import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AppButtonComponent } from '@app/components/app-button/app-button.component';
import { take } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { NgOptimizedImage } from '@angular/common';
import { recordRumEvent } from '../../monitoring/aws-rum';
import { ClientDiagnosticsService } from '../../services/client-diagnostics.service';
import { SessionStateResetService } from '../../state/session-state-reset.service';

@Component({
  selector: 'app-forbidden',
  imports: [AppButtonComponent, NgOptimizedImage],
  templateUrl: './forbidden.component.html',
  styleUrl: './forbidden.component.scss',
})
export class ForbiddenComponent {
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly clientDiagnostics = inject(ClientDiagnosticsService);
  private readonly sessionState = inject(SessionStateResetService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private cookieService = inject(CookieService);

  get isAccessDenied(): boolean {
    return this.route.snapshot.queryParamMap.get('reason') === 'access-denied';
  }

  get actionLabel(): string {
    return this.isAccessDenied ? 'Back' : 'Go To Login';
  }

  login(): void {
    if (this.isAccessDenied) {
      const requestedReturnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
      const returnUrl =
        requestedReturnUrl?.startsWith('/') && requestedReturnUrl !== '/forbidden' ? requestedReturnUrl : '/invoices';
      void this.router.navigateByUrl(returnUrl);
      return;
    }

    recordRumEvent('auth.logout.requested', {
      app: 'front',
      source: 'forbidden_relogin',
      hasSessionStorage: !!window.sessionStorage,
    });
    this.clientDiagnostics.track(
      'auth.logout.requested',
      {
        source: 'forbidden_relogin',
        hasSessionStorage: !!window.sessionStorage,
      },
      'warn',
    );
    this.clientDiagnostics.flushNow();
    this.sessionState.reset();

    if (window.sessionStorage) {
      window.sessionStorage.clear();
    }
    this.cookieService.deleteAll();

    this.oidcSecurityService
      .logoff()
      .pipe(take(1))
      .subscribe(() => {
        // Cognito Logout URL
        const clientId = environment.cognitoClientId;
        const logoutUri = encodeURIComponent(window.location.origin);
        const cognitoDomain = environment.cognitoDomain;

        window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;
      });
  }
}
